import { PanelLeftClose, PanelLeftOpen, Radar } from "lucide-react"
import { NavLink } from "react-router-dom"

import { NAV } from "@/components/layout/nav"
import { InfoTip } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

/**
 * Левое меню. Две раскладки в одном компоненте:
 *   • `md+` — колонна в потоке, сворачивается кнопкой Collapse (ширина 216/56px);
 *   • `< md` — выдвижная шторка поверх контента (`fixed`), в поток не встаёт,
 *     ширину вьюпорта не ест. На узком экране всегда развёрнута (свёрнутый вид с
 *     одними иконками на телефоне бесполезен), а «свернуть» там прячется.
 */
export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onCloseMobile,
}: {
  collapsed: boolean
  onToggle: () => void
  mobileOpen: boolean
  onCloseMobile: () => void
}) {
  return (
    <nav
      aria-label="Sections"
      className={cn(
        // База — шторка на телефоне: фиксирована, задвинута за левый край,
        // выезжает при `mobileOpen`. Ширину держим фиксированной (216px), в
        // свёрнутый режим на узком экране не уходим.
        "fixed inset-y-0 left-0 z-40 flex w-[216px] shrink-0 flex-col bg-ink transition-transform duration-200 ease-snap",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // `md+` — обычная колонна в потоке, с сворачиванием по ширине.
        "md:static md:z-auto md:translate-x-0 md:transition-[width]",
        collapsed ? "md:w-14" : "md:w-[216px]",
      )}
    >
      <div
        className={cn(
          "flex h-12 items-center gap-2 px-4 text-approve",
          collapsed && "md:justify-center md:px-0",
        )}
      >
        <Radar className="h-5 w-5 shrink-0" strokeWidth={2.25} />
        <span
          className={cn(
            "font-logo text-base font-semibold uppercase tracking-[0.18em]",
            collapsed && "md:hidden",
          )}
        >
          paydetect
        </span>
      </div>

      <ul className="flex flex-1 flex-col gap-px overflow-y-auto px-2 py-2">
        {NAV.map((item) => {
          const link = (
            <NavLink
              to={item.path}
              onClick={onCloseMobile}
              // `end` только для корня: иначе `/` подсвечивался бы на всех
              // страницах, а `/scoring` гас бы на `/scoring/default`.
              end={item.path === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded px-2 py-2 text-sm font-medium transition-colors duration-150 ease-snap",
                  collapsed && "md:justify-center md:px-0",
                  isActive
                    ? "bg-ink-raised text-approve"
                    : "text-ink-fg/85 hover:bg-ink-raised/70 hover:text-ink-fg",
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span className={cn("truncate", collapsed && "md:hidden")}>{item.label}</span>
            </NavLink>
          )

          return (
            <li key={item.path} className={cn(item.gapBefore && "mt-4")}>
              {/* Подсказка с названием нужна только в свёрнутой колонне на
                  десктопе, где у пункта видна одна иконка. На телефоне меню
                  всегда развёрнуто (текст рядом), поэтому подсказку там не
                  вешаем — оборачиваем в InfoTip лишь при `collapsed`, а он
                  действует только на `md+`. */}
              {collapsed ? <InfoTip label={item.label}>{link}</InfoTip> : link}
            </li>
          )
        })}
      </ul>

      {/* Кнопка Collapse — только для колонны на десктопе. На телефоне шторка
          гасится затемнением, а не сворачивается в полоску иконок. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className={cn(
          "m-2 hidden items-center gap-2.5 rounded px-2 py-2 text-sm font-medium text-ink-fg/85 transition-colors duration-150 ease-snap hover:bg-ink-raised/70 hover:text-ink-fg md:flex",
          collapsed && "md:justify-center md:px-0",
        )}
      >
        {collapsed ? (
          <PanelLeftOpen className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        ) : (
          <PanelLeftClose className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        )}
        <span className={cn(collapsed && "md:hidden")}>Collapse</span>
      </button>
    </nav>
  )
}
